/**
 * Equipment detail — everything about one catalogue item that isn't its name,
 * rate, or quantity (#333).
 *
 * Adding an item is four fields in a modal; this page is where the rest lives.
 *
 * Layout: the H1 names the page ("Equipment detail"), with the item's name
 * directly under it as a secondary editable line — the same editable-title
 * input the event workspace header uses, one step down the type scale. The
 * name alone was the title until an Operator landing here mid-task had nothing
 * telling them which route they were on.
 *
 * The facts an Operator sets on every item — rate, quantity, storage spot, and
 * whether it is hired in — edit right on the page. The two optional bodies of
 * detail (physical measurements, Role ladders)
 * live behind drill-down rows (the pattern from Documents & emails), with a
 * back link inside: an item that is never trucked and never role-backed should
 * not make the reader scroll past nine blank fields to reach the delete button.
 * The page runs the standard admin max-w-7xl column — title, fields, and
 * actions share its edges — and everything inside runs to that column's width,
 * with no per-block max-widths.
 *
 * Inside a space the fields are labelled inputs, not settings rows: the
 * settings-row shape (title far left, control far right) suits prose settings
 * and strands a 2-character number half a page from its label.
 *
 * Save model is unchanged: every control commits itself on blur/change, there
 * is no page Save button, and a failure renders on the field that caused it,
 * never at the page top.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  HeaderActions,
  type HeaderAction,
} from "../components/ui/HeaderActions";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { XIcon } from "../components/ui/icons";
import { Select } from "../components/ui/Select";
import { ToggleSettingRow } from "../components/ui/SettingRow";
import { RoleLadderEditor } from "../components/admin/RoleLadderEditor";
import forms from "../components/ui/field.module.css";
import { cx } from "@shared/cx";
import { checkField, type Rule } from "@shared/validators";
import {
  nameLengthRule,
  nonNegIntRule,
  nonNegNumberRule,
  qtyFormatRule,
  rateRule,
} from "../components/admin/validators";
import { useEditableField } from "../lib/use-editable-field";
import {
  createGearRole,
  deleteEquipment,
  deleteGearRole,
  getEquipment,
  listGearRoles,
  patchEquipmentRelations,
  renameGearRole,
  type EquipmentItem,
  type EquipmentRelationsPatch,
  type GearRole,
} from "../lib/equipment-api";
import { listSuppliers, type Supplier } from "../lib/suppliers-api";
import {
  listWarehouses,
  putWarehouseStock,
  type CompanyWarehouse,
} from "../lib/warehouses-api";
import { Tooltip } from "../components/ui/Tooltip";
import {
  BackLink,
  DrillDownRow,
  FieldLabel,
  Loading,
  SectionHeader,
} from "./settings/section-chrome";
import { InlineBanner } from "../components/ui/InlineBanner";
import chrome from "./settings/settings.module.css";
import page from "./page.module.css";
import styles from "./equipment-detail.module.css";

/** Blank persists as explicit null — unknown, never zero. */
const optionalNumber = (v: string): number | null =>
  v.trim() === "" ? null : Number(v.trim());

type SpaceID = "physical" | "roles";

/** Shared by the drill-down row and the space it opens, so they can't drift. */
const PHYSICAL_DESCRIPTION =
  "Optional. Size, weight, and insured value — what the vehicle check uses. Blank means unknown, never zero.";

const BACK_LABEL = "All item settings";

export default function EquipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [space, setSpace] = useState<SpaceID | null>(null);
  const [item, setItem] = useState<EquipmentItem | null>(null);
  const [gearRoles, setGearRoles] = useState<GearRole[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<CompanyWarehouse[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (item) document.title = `${item.name} — Rental Admin`;
  }, [item]);

  useEffect(() => {
    if (!id) return;
    const ac = new AbortController();
    Promise.all([
      getEquipment(id, ac.signal),
      listGearRoles(ac.signal),
      listSuppliers(ac.signal),
      // Warehouses (#304). A Company with one site is every Company before
      // this issue, and the per-warehouse block below stays hidden for them.
      listWarehouses(ac.signal).catch(() => [] as CompanyWarehouse[]),
    ])
      .then(([nextItem, roles, sups, whs]) => {
        setItem(nextItem);
        setGearRoles(roles);
        setSuppliers(sups);
        setWarehouses(whs);
        setLoadError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => ac.abort();
  }, [id]);

  /**
   * The commit every row calls. THROWS on failure — that is the contract the
   * SettingRow children rely on to render the inline row error, so never
   * swallow here. Roles are refetched alongside the item because a membership
   * write reshuffles other items' ladder positions too.
   */
  const patch = useCallback(
    async (partial: EquipmentRelationsPatch) => {
      if (!id) return;
      await patchEquipmentRelations(id, partial);
      const [nextItem, roles] = await Promise.all([
        getEquipment(id),
        listGearRoles(),
      ]);
      setItem(nextItem);
      setGearRoles(roles);
    },
    [id],
  );

  /**
   * Picker "Create '<typed>'" row (#320): create the role, then assign this
   * item as its first member — one gesture, so the new role is never left
   * empty. Single-profile companies only — a bare name can't say which
   * profile a multi-profile company means.
   */
  const singleProfile = new Set(gearRoles.map((r) => r.profile_key)).size <= 1;
  const createRoleWithThisItem = useCallback(
    async (displayName: string) => {
      if (!id) return;
      const role = await createGearRole(displayName);
      const assignments = gearRoles
        .filter((r) => r.members.some((m) => m.equipment_id === id))
        .map((r) => ({
          profile_key: r.profile_key,
          role_key: r.role_key,
          sort_order: r.members.find((m) => m.equipment_id === id)!.sort_order,
        }));
      assignments.push({
        profile_key: role.profile_key,
        role_key: role.role_key,
        sort_order: 0,
      });
      await patch({ chain_assignments: assignments });
    },
    [id, gearRoles, patch],
  );

  // Rename / delete live in the picker rows (#320) — the roles refetch so
  // every ladder heading picks up the change.
  const renameRole = useCallback(
    async (role: GearRole, displayName: string) => {
      await renameGearRole(role.profile_key, role.role_key, displayName);
      setGearRoles(await listGearRoles());
    },
    [],
  );
  const removeRole = useCallback(async (role: GearRole) => {
    await deleteGearRole(role.profile_key, role.role_key);
    setGearRoles(await listGearRoles());
  }, []);

  // The one gate for every per-warehouse control on this page (#304): above
  // one active site, stock is the split's sum and is edited per row below.
  const activeWarehouses = warehouses.filter((w) => w.active);
  const splitStock = activeWarehouses.length > 1;

  const remove = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deleteEquipment(id);
      void navigate("/equipment");
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
      setDeleting(false);
    }
  };

  const headerActions: HeaderAction[] = [
    { kind: "link", to: "/equipment", label: "Back" },
  ];

  return (
    <div className={page.page}>
      {/* The standard admin page column (max-w-7xl): the title, every field,
          and the actions share its left and right edges, so this page frames
          like every other admin route. */}
      <div className={page.content}>
        <div className={styles.header}>
          {/* The H1 names the PAGE, not the record: an editable item name on
              its own left the reader with no clue which route they were on.
              The name sits under it as a secondary editable line — still the
              page's subject, no longer its only label. */}
          <div className={styles.headerText}>
            <h1 className={styles.title}>
              Equipment detail
            </h1>
            {item && (
              <div className={styles.nameLine}>
                <TitleField name={item.name} patch={patch} />
              </div>
            )}
          </div>
          <HeaderActions actions={headerActions} />
        </div>

        {loadError && (
          <Banner
            variant="error"
            className={chrome.banner}
            onDismiss={() => setLoadError(null)}
          >
            {loadError}
          </Banner>
        )}

        {!item ? (
          !loadError && <Loading />
        ) : (
          <section>
            {space && (
              <BackLink label={BACK_LABEL} onClick={() => setSpace(null)} />
            )}

            {space === null && (
              <>
                {/* The item's own facts, edited in place — no drill-down for a
                    handful of fields. The page title already carries the name;
                    nothing here repeats it. */}
                <div className={styles.facts}>
                  <EditableField
                    id="detail-rate"
                    label="Hire rate / day"
                    tooltip="Per-day price this item contributes to a quote line."
                    value={String(item.hire_rate_per_day)}
                    rule={rateRule}
                    numeric
                    required
                    onSave={(next) =>
                      patch({ hire_rate_per_day: Number(next) })
                    }
                  />
                  {/* With more than one warehouse the quantity is the sum of
                      the rows below, not a field: editing it here could only
                      guess which site the change belonged to. */}
                  {splitStock ? (
                    <div>
                      <p className={forms.label}>
                        <Tooltip content="The total across your warehouses — set it per warehouse below.">
                          Stock quantity
                        </Tooltip>
                      </p>
                      <p className={styles.readOnlyValue}>
                        {item.stock_quantity}
                      </p>
                    </div>
                  ) : (
                    <EditableField
                      id="detail-qty"
                      label="Stock quantity"
                      tooltip="Owned stock count the picker counts against availability."
                      value={String(item.stock_quantity)}
                      rule={qtyFormatRule}
                      numeric
                      required
                      onSave={(next) => patch({ stock_quantity: Number(next) })}
                    />
                  )}
                  <EditableField
                    id="detail-location"
                    label="Storage spot"
                    tooltip="Where it sits in the warehouse — shown on the job sheet's pull list so crew know where to find it."
                    optional
                    value={item.storage_location ?? ""}
                    onSave={(next) =>
                      patch({ storage_location: next.trim() || null })
                    }
                  />
                </div>

                {/* Where that stock physically is (#304). Only drawn once the
                    Company runs more than one site — a single-warehouse
                    Company sees the three fields above and nothing else,
                    exactly as before. */}
                {splitStock && id && (
                  <div className={styles.block}>
                    <WarehouseStock
                      equipmentId={id}
                      warehouses={activeWarehouses}
                      item={item}
                      onSaved={async () => {
                        setItem(await getEquipment(id));
                      }}
                    />
                  </div>
                )}

                {/* Hired-in sits with the fields above it: it changes what
                    stock quantity MEANS, so it belongs beside it rather than
                    below two blocks of optional detail. */}
                <div className={styles.block}>
                  <SourcingRows
                    item={item}
                    suppliers={suppliers}
                    patch={patch}
                  />
                </div>

                {/* The optional bodies of detail, in the order an Operator
                    reaches for them — measurements before Role ladders. No
                    group heading: the rows name themselves. */}
                <div className={styles.block}>
                  <ul className={chrome.listBordered}>
                    <DrillDownRow
                      name="Physical"
                      description={PHYSICAL_DESCRIPTION}
                      onOpen={() => setSpace("physical")}
                    />
                    <DrillDownRow
                      name="Gear Roles"
                      description="Which Roles this item can fill, and the order the picker tries it."
                      onOpen={() => setSpace("roles")}
                    />
                  </ul>
                </div>

                {/* A one-shot destructive action on the thing this page is
                    about — a plain row, unboxed like the rest of the page. */}
                <div className={styles.deleteRow}>
                  <p className={styles.deleteText}>
                    Delete this item from the catalogue.
                  </p>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteOpen(true);
                    }}
                  >
                    Delete item
                  </Button>
                </div>
              </>
            )}

            {space === "physical" && (
              <>
                <SectionHeader
                  title="Physical"
                  description={PHYSICAL_DESCRIPTION}
                />
                <PhysicalFields item={item} patch={patch} />
              </>
            )}

            {space === "roles" && (
              <>
                <SectionHeader
                  title="Gear Roles"
                  description="A Role is a requirement; the ladder is the order the picker tries this item against the others."
                />
                <RoleLadderEditor
                  itemId={item.id}
                  itemName={item.name}
                  gearRoles={gearRoles}
                  onCreateRole={
                    singleProfile ? createRoleWithThisItem : undefined
                  }
                  onRenameRole={renameRole}
                  onDeleteRole={removeRole}
                  onCommit={(payload) =>
                    patch({
                      chain_assignments: payload.chain_assignments,
                      // Absent unless a ladder's OTHER members were reordered:
                      // the key would otherwise rewrite untouched roles.
                      ...(payload.role_orders.length > 0
                        ? { role_orders: payload.role_orders }
                        : {}),
                    })
                  }
                />
              </>
            )}
          </section>
        )}

        <ConfirmModal
          open={deleteOpen}
          title="Delete item"
          message={`Delete "${item?.name ?? ""}"? It disappears from the catalogue and from every Role ladder.`}
          confirmLabel="Delete"
          loading={deleting}
          error={deleteError}
          onConfirm={() => void remove()}
          onCancel={() => setDeleteOpen(false)}
        />
      </div>
    </div>
  );
}

/**
 * Where this item's stock physically sits, per Warehouse (#304).
 *
 * The shelf field above says where on a site; this says which site. One row
 * per active Warehouse — a site holding none of the item is a visible zero,
 * not a missing row, because "Auckland has none" is the answer an Operator
 * came here for.
 *
 * There is no total to reconcile: `stock_quantity` is the DB-derived sum of
 * these rows, so the two cannot disagree and nothing here restates it.
 */
function WarehouseStock({
  equipmentId,
  warehouses,
  item,
  onSaved,
}: {
  equipmentId: string;
  warehouses: CompanyWarehouse[];
  item: EquipmentItem;
  onSaved: () => Promise<void>;
}) {
  const byWarehouse = new Map(
    item.warehouse_stock.map((s) => [s.warehouse_id, s.quantity]),
  );

  return (
    <div>
      <h2 className={styles.sectionTitle}>Warehouses</h2>
      <p className={styles.sectionDescription}>
        How this item's stock splits across your sites. A job served from a
        warehouse can only draw on that warehouse's number.
      </p>

      <ul className={cx(chrome.listBordered, styles.listGap)}>
        {warehouses.map((wh) => (
          <WarehouseStockRow
            key={wh.id}
            equipmentId={equipmentId}
            warehouse={wh}
            quantity={byWarehouse.get(wh.id) ?? 0}
            onSaved={onSaved}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * One Warehouse's quantity. Commits on blur like every other setting row; a
 * blank clears the warehouse's row entirely rather than storing a 0, so
 * "holds none" has exactly one representation.
 */
function WarehouseStockRow({
  equipmentId,
  warehouse,
  quantity,
  onSaved,
}: {
  equipmentId: string;
  warehouse: CompanyWarehouse;
  quantity: number;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState(String(quantity));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(String(quantity));
  }, [quantity]);

  const commit = async () => {
    const trimmed = draft.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (next !== null && (!Number.isInteger(next) || next < 0)) {
      setError("Whole numbers only.");
      return;
    }
    if ((next ?? 0) === quantity && trimmed !== "") return;
    setSaving(true);
    setError(null);
    try {
      await putWarehouseStock(equipmentId, warehouse.id, next);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setDraft(String(quantity));
    } finally {
      setSaving(false);
    }
  };

  const inputId = `warehouse-stock-${warehouse.id}`;
  return (
    <li className={chrome.row}>
      <div className={styles.stockText}>
        <label
          htmlFor={inputId}
          className={styles.stockName}
        >
          {warehouse.name}
        </label>
        {warehouse.address !== "" && (
          <p className={chrome.rowMeta}>{warehouse.address}</p>
        )}
        {error && <InlineBanner variant="error">{error}</InlineBanner>}
      </div>
      {/* The width lives on the wrapper -- see `.stockControl`. */}
      <div className={styles.stockControl}>
        <input
          id={inputId}
          type="number"
          min={0}
          inputMode="numeric"
          aria-label={`Stock at ${warehouse.name}`}
          className={cx(
            forms.input,
            saving ? styles.stockInputSaving : styles.stockInput,
          )}
          disabled={saving}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </div>
    </li>
  );
}

/**
 * The item's name, under the page H1 — the same editable-title treatment the
 * event workspace header uses (transparent-border input, commits on blur), one
 * step down the type scale so it reads as the page's subject rather than its
 * label. Blank never saves (the name is required).
 */
function TitleField({
  name,
  patch,
}: {
  name: string;
  patch: (partial: EquipmentRelationsPatch) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const field = useEditableField(name, {
    validate: (v) => checkField(v, nameLengthRule),
    onSave: (next) => {
      if (!next) return;
      setSaving(true);
      void patch({ name: next })
        .then(() => setSaveError(null))
        .catch((err) =>
          setSaveError(err instanceof Error ? err.message : String(err)),
        )
        .finally(() => setSaving(false));
    },
  });
  const error = field.error ?? saveError;
  return (
    <>
      <input
        type="text"
        value={field.draft}
        onChange={(e) => field.setDraft(e.target.value)}
        onFocus={field.onFocus}
        onBlur={field.onBlur}
        aria-label="Name"
        aria-invalid={Boolean(field.error) || undefined}
        disabled={saving}
        className={cx(styles.titleInput, field.error && styles.titleInvalid)}
      />
      {error && (
        <InlineBanner variant="error" className={styles.titleError}>
          {error}
        </InlineBanner>
      )}
    </>
  );
}

/* ─── Physical facts (#189) ─────────────────────────────────────────────── */

/**
 * Dimensions read as a set, so they sit on one row of three; weight and value
 * are unrelated scalars and pair off below. Five stacked full-width rows made
 * the reader scroll a screen for what is really two facts.
 */
function PhysicalFields({
  item,
  patch,
}: {
  item: EquipmentItem;
  patch: (partial: EquipmentRelationsPatch) => Promise<void>;
}) {
  const dim = (
    id: string,
    label: string,
    key: "length_mm" | "width_mm" | "height_mm",
    tooltip: string,
  ) => (
    <EditableField
      id={id}
      label={label}
      tooltip={tooltip}
      value={item[key] != null ? String(item[key]) : ""}
      rule={nonNegIntRule}
      numeric
      onSave={(next) => patch({ [key]: optionalNumber(next) })}
    />
  );

  return (
    <div className={styles.physical}>
      <div className={styles.dims}>
        {dim(
          "detail-length",
          "Length (mm)",
          "length_mm",
          "Packed length per unit, used with width and height for the item's volume.",
        )}
        {dim(
          "detail-width",
          "Width (mm)",
          "width_mm",
          "Packed width per unit.",
        )}
        {dim(
          "detail-height",
          "Height (mm)",
          "height_mm",
          "Packed height per unit.",
        )}
      </div>
      <div className={styles.pair}>
        <EditableField
          id="detail-weight"
          label="Weight (kg)"
          tooltip="Per-unit weight, used to check gear fits the booked vehicles."
          value={item.weight_kg != null ? String(item.weight_kg) : ""}
          rule={nonNegNumberRule}
          numeric
          onSave={(next) => patch({ weight_kg: optionalNumber(next) })}
        />
        <EditableField
          id="detail-insurable"
          label="Insured value"
          tooltip="Replacement value per unit for insurance disclosure."
          value={
            item.insurable_value != null ? String(item.insurable_value) : ""
          }
          rule={nonNegNumberRule}
          numeric
          onSave={(next) => patch({ insurable_value: optionalNumber(next) })}
        />
      </div>
      {item.volume_m3 != null && (
        <p className={styles.volume}>
          Volume {item.volume_m3.toFixed(3)} m³ per unit
        </p>
      )}
    </div>
  );
}

/**
 * A labelled field that commits itself on blur (or Enter) — the record-editor
 * counterpart of a settings row, with the label directly above its input rather
 * than at the far side of the page.
 *
 * An unchanged value never fires a save. A `rule` is checked first: a malformed
 * entry shows its message and stays put rather than saving something the field
 * never meant. `required` fields revert on blank; optional ones persist blank
 * as whatever the caller's onSave maps it to (null, for every field here).
 */
function EditableField({
  id,
  label,
  tooltip,
  value,
  rule,
  onSave,
  numeric = false,
  required = false,
  optional = false,
}: {
  id: string;
  label: string;
  /** One sentence on what the value affects; hangs off the label. */
  tooltip?: string;
  value: string;
  rule?: Rule;
  onSave: (next: string) => Promise<void>;
  numeric?: boolean;
  required?: boolean;
  optional?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setDraft(value), [value]);

  const commit = async () => {
    if (draft === value) return;
    if (required && draft.trim() === "") {
      setDraft(value);
      setError(null);
      return;
    }
    const invalid = rule ? checkField(draft, rule) : null;
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.field}>
      <FieldLabel htmlFor={id} tooltip={tooltip}>
        {label}
        {optional && (
          <span className={forms.optional}> (optional)</span>
        )}
      </FieldLabel>
      <input
        id={id}
        type="text"
        inputMode={numeric ? "decimal" : undefined}
        className={forms.input}
        value={draft}
        disabled={saving}
        aria-invalid={Boolean(error) || undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      {error && <InlineBanner variant="error">{error}</InlineBanner>}
    </div>
  );
}

/* ─── Suppliers (link only) ─────────────────────────────────────────────── */

/**
 * Links this item to suppliers who can source it, right on the page. Create/
 * edit/delete of the suppliers themselves lives in Settings → Suppliers: a
 * supplier is a Company-wide directory entry, not a property of one item.
 */
function SourcingRows({
  item,
  suppliers,
  patch,
}: {
  item: EquipmentItem;
  suppliers: Supplier[];
  patch: (partial: EquipmentRelationsPatch) => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const linked = item.supplier_ids;
  const linkedSuppliers = suppliers.filter((s) => linked.includes(s.id));
  const selectable = suppliers
    .filter((s) => !linked.includes(s.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const setLinks = async (next: string[]) => {
    setBusy(true);
    try {
      await patch({ supplier_ids: next });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <ToggleSettingRow
        id="detail-hired-in"
        title="Hired in"
        description="We don't own this — the picker treats it as unbounded stock."
        checked={item.is_hired_in}
        onChange={(next) =>
          // Clearing hired-in clears the supplier links with it.
          patch({ is_hired_in: next, ...(next ? {} : { supplier_ids: [] }) })
        }
      />

      {item.is_hired_in && (
        <div className={styles.dependent}>
          {error && (
            <p role="alert" className={styles.dependentError}>
              {error}
            </p>
          )}

          {linkedSuppliers.length > 0 && (
            <ul className={styles.supplierList}>
              {linkedSuppliers.map((s) => (
                <li
                  key={s.id}
                  className={styles.supplierRow}
                >
                  <div className={chrome.rowText}>
                    <p className={chrome.rowName}>
                      {s.name}
                    </p>
                    <p className={chrome.rowMeta}>
                      {[s.email, s.phone].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    aria-label={`Unlink ${s.name}`}
                    className={styles.unlink}
                    onClick={() =>
                      void setLinks(linked.filter((sid) => sid !== s.id))
                    }
                  >
                    <XIcon className={chrome.icon} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {suppliers.length === 0 ? (
            <p className={styles.emptyText}>
              No suppliers yet — add them in{" "}
              <Link to="/settings" className={styles.settingsLink}>
                Settings → Suppliers
              </Link>
              .
            </p>
          ) : (
            <div className={styles.supplierPicker}>
              <label className={forms.label} htmlFor="detail-supplier-add">
                Add a supplier
              </label>
              <Select
                id="detail-supplier-add"
                value=""
                disabled={busy || selectable.length === 0}
                onChange={(next) => {
                  if (next) void setLinks([...linked, next]);
                }}
                options={[
                  {
                    value: "",
                    label:
                      selectable.length === 0
                        ? "All suppliers linked"
                        : "Select…",
                  },
                  ...selectable.map((s) => ({ value: s.id, label: s.name })),
                ]}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
